import {
  AddProductsToCurrentCartSucceededAction,
  AddToCurrentCartSucceededAction,
  DeleteCurrentCartEntrySucceededAction,
  UpdateCurrentCartEntryQuantitySucceededAction,
  addProductsToCurrentCartSucceededType,
  addToCurrentCartSucceededType,
  deleteCurrentCartEntrySucceededType,
  updateCurrentCartEntryQuantitySucceededType
} from '../../carts/cartsSlice';
import {
  CartEntriesDifferences,
  calculateCartEntriesDifferences
} from './calculateCartEntriesDifferences';
import { HydrateAction, hydrateType } from '../../HydrateAction';
import { OptionalStateCarts, StateCart, StateCartEntry } from '../../carts/CartsState';
import { SagaGenerator, actionChannel, call, select, take } from 'typed-redux-saga';
import trackCartActions, {
  CartActionType,
  TrackCartActionsType
} from '../../../tracking/trackCartActions';
import trackFiltersActions, { FiltersEventType } from '../../../tracking/trackFiltersActions';
import { AddToCartEventItem } from '../../../tracking/eCommerceTrackingEventSchema';
import { AddedSearchFilters } from '../../search/SearchState';
import { CartTypeQuantityData } from '../../../tracking/pushToDataLayer';
import { Channel } from 'redux-saga';
import { CustomOrderType } from '../../carts/CustomOrderType';
import { calculateCartQuantities } from '../../../tracking/calculateCartQuantities';
import { convertCartEntryToAddToCartEventItem } from '../../../tracking/convertCartEntryToAddToCartEventItem';
import roundToTwo from '../../../roundToTwo';
import selectAuthenticatedUserId from '../../auth/selectors/selectAuthenticatedUserId';
import selectCarts from '../../carts/selectors/selectCarts';
import selectCountryCode from '@spa/lib/store/settings/selectors/selectCountryCode';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';
import selectLastAddedProductFilters from '../../search/selectors/selectLastAddedProductFilters';
import selectLastQueryMetadata from '../../search/selectors/selectLastQueryMetadata';
import selectPathname from '@spa/lib/store/router/selectors/selectPathname';
import selectPricesIncludeTax from '@spa/lib/store/settings/selectors/selectPricesIncludeTax';
import selectSessionUserId from '../../session/selectors/selectSessionUserId';
import { tracker } from '../../../tracking/tracker';

const cartUpdateActionTypes: Array<string> = [
  hydrateType,
  addToCurrentCartSucceededType,
  addProductsToCurrentCartSucceededType,
  deleteCurrentCartEntrySucceededType,
  updateCurrentCartEntryQuantitySucceededType
];

type CartUpdateActionTypes =
  | HydrateAction
  | AddToCurrentCartSucceededAction
  | AddProductsToCurrentCartSucceededAction
  | DeleteCurrentCartEntrySucceededAction
  | UpdateCurrentCartEntryQuantitySucceededAction;

function* handleCartStateChanges(
  oldCartState: StateCart | null,
  currentCartState: StateCart | null,
  customOrderType: CustomOrderType,
  anonymousUserId: string | null,
  authenticatedUserId: string | null,
  pathname: string,
  lastQueryMetadata: Record<string, string>,
  lastAddedProductFilters: AddedSearchFilters | undefined,
  session: CartTypeQuantityData | undefined,
  shouldTrackAddedEntries: boolean
): SagaGenerator<void> {
  if (
    currentCartState === null ||
    oldCartState === null ||
    currentCartState.code === oldCartState.code
  ) {
    const newEntries: ReadonlyArray<StateCartEntry> = currentCartState?.entries ?? [];
    const { addedEntries, removedEntries }: CartEntriesDifferences =
      calculateCartEntriesDifferences(oldCartState?.entries ?? [], newEntries);

    const baseCartForTracking: StateCart | null = currentCartState ?? oldCartState;

    if (baseCartForTracking !== null) {
      const country: string = yield* select(selectCountryCode);
      const { preOrderEnabled } = yield* select(selectGeneralSettings);
      const pricesIncludeTax: boolean = yield* select(selectPricesIncludeTax);

      if (shouldTrackAddedEntries && addedEntries.length > 0) {
        const addToCartItems: Array<AddToCartEventItem> = addedEntries.map((entry, index) =>
          convertCartEntryToAddToCartEventItem(
            entry,
            index,
            country,
            preOrderEnabled,
            pricesIncludeTax
          )
        );

        const value: number = roundToTwo(
          addedEntries.reduce((acc, entry) => acc + entry.totalPrice.valueWithoutTax, 0)
        );

        const anonymousId: string = yield* select(selectSessionUserId);
        const authenticatedId: string | null = yield* select(selectAuthenticatedUserId);

        yield* call(tracker.addToCart, {
          anonymousId,
          authenticatedId,
          ecommerce: {
            currency: baseCartForTracking.totalPrice.currencyIso,
            value,
            items: addToCartItems
          }
        });

        if (lastAddedProductFilters && lastAddedProductFilters.filters.length > 0) {
          const isSameProduct: boolean =
            lastAddedProductFilters.productCode === newEntries[newEntries.length - 1]?.product.code;
          if (isSameProduct) {
            yield* call(
              trackFiltersActions,
              FiltersEventType.CONVERTED_SELECTED_ADD_TO_CART,
              lastAddedProductFilters.filters,
              anonymousUserId,
              authenticatedUserId,
              lastQueryMetadata
            );
          }
        }
      }

      if (removedEntries.length > 0) {
        const actionType: TrackCartActionsType = { type: CartActionType.REMOVE_FROM_CART };

        yield* call(
          trackCartActions,
          actionType,
          { ...baseCartForTracking, entries: removedEntries },
          customOrderType,
          country,
          anonymousUserId,
          authenticatedUserId,
          pathname,
          lastQueryMetadata,
          session
        );
      }
    }
  }
}

function* watchCartActionsTrackingSaga(): SagaGenerator<void> {
  const actionChannelForCartUpdates: Channel<CartUpdateActionTypes> =
    yield* actionChannel<CartUpdateActionTypes>(cartUpdateActionTypes);

  let oldCartsState: OptionalStateCarts | undefined;
  while (true) {
    const action: CartUpdateActionTypes = yield* take(actionChannelForCartUpdates);
    if (action.type === hydrateType) {
      oldCartsState = yield* select(selectCarts);
    } else {
      const currentCartState: OptionalStateCarts = yield* select(selectCarts);
      const lastQueryMetadata: Record<string, string> = yield* select(selectLastQueryMetadata);
      const anonymousUserId: string | null = yield* select(selectSessionUserId);
      const authenticatedUserId: string | null = yield* select(selectAuthenticatedUserId);
      const lastAddedProductFilters: AddedSearchFilters | undefined = yield* select(
        selectLastAddedProductFilters
      );
      const pathname: string = yield* select(selectPathname);
      const cartQuantityData = calculateCartQuantities(currentCartState);
      // Bulk adds are also used to move entries between cart types; only opted-in ones are adds.
      const shouldTrackAddedEntries: boolean =
        action.type !== addProductsToCurrentCartSucceededType ||
        action.payload.trackAddToCart === true;

      for (const orderType of Object.values(CustomOrderType)) {
        let oldCartStateForType: StateCart | null = null;
        if (oldCartsState) {
          oldCartStateForType = oldCartsState[orderType];
        }
        const currentCartStateForType: StateCart | null = currentCartState[orderType];

        yield* handleCartStateChanges(
          oldCartStateForType,
          currentCartStateForType,
          orderType,
          anonymousUserId,
          authenticatedUserId,
          pathname,
          lastQueryMetadata,
          lastAddedProductFilters,
          cartQuantityData,
          shouldTrackAddedEntries
        );
      }

      oldCartsState = currentCartState;
    }
  }
}

export default watchCartActionsTrackingSaga;
